import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { GlobalStateService } from '../global/global-state.service';
import { VehiclePriceSheet, VehiclePriceSheetResponse } from '../../shared/models/vehicle-price-sheet.interface';

@Injectable({
  providedIn: 'root'
})
export class VehiclePriceSheetService {

  private service_type = "vehicle-price-sheets";
  public baseURL = '';

  constructor(
    private _globalStateService: GlobalStateService, 
    private _http: HttpClient
  ) {
    this.baseURL = this._globalStateService.GetBaseEndpoint('base');
  }

  /**
   * Get all vehicle price sheets with pagination and filters
   */
  GetData(page_number: string, search: string = '', filters: Record<string, any> = {}): Observable<VehiclePriceSheetResponse> {
    let params = new URLSearchParams(filters).toString();
    const url = `${this.baseURL}/${this.service_type}?page=${page_number}&search=${search}&${params}`;
    return this._http.get<VehiclePriceSheetResponse>(url);
  }

  /**
   * Get vehicle price sheet by ID
   */
  GetDataById(id: any): Observable<VehiclePriceSheetResponse> {
    const url = `${this.baseURL}/${this.service_type}/${id}`;
    return this._http.get<VehiclePriceSheetResponse>(url);
  }

  /**
   * Get vehicle price sheet by vehicle ID
   */
  GetDataByVehicleId(vehicleId: string): Observable<VehiclePriceSheetResponse> {
    const url = `${this.baseURL}/${this.service_type}/vehicle/${vehicleId}`;
    return this._http.get<VehiclePriceSheetResponse>(url);
  }

  /**
   * Create new vehicle price sheet
   */
  AddData(data: VehiclePriceSheet): Observable<VehiclePriceSheetResponse> {
    const url = `${this.baseURL}/${this.service_type}`;
    return this._http.post<VehiclePriceSheetResponse>(url, data);
  }

  /**
   * Update existing vehicle price sheet
   */
  UpdateData(id: string, data: VehiclePriceSheet): Observable<VehiclePriceSheetResponse> {
    const url = `${this.baseURL}/${this.service_type}/${id}`;
    return this._http.patch<VehiclePriceSheetResponse>(url, data);
  }

  /**
   * Delete vehicle price sheet by ID
   */
  DeleteDataById(id: any): Observable<VehiclePriceSheetResponse> {
    const url = `${this.baseURL}/${this.service_type}/${id}`;
    return this._http.delete<VehiclePriceSheetResponse>(url);
  }

  /**
   * Calculate subtotals for a section
   */
  calculateSectionSubtotal(sectionData: any): { actual: number; estimated: number; difference: number } {
    let actualTotal = 0;
    let estimatedTotal = 0;

    Object.keys(sectionData.actual || {}).forEach(key => {
      if (typeof sectionData.actual[key] === 'number') {
        actualTotal += sectionData.actual[key] || 0;
      }
    });

    Object.keys(sectionData.estimated || {}).forEach(key => {
      if (typeof sectionData.estimated[key] === 'number') {
        estimatedTotal += sectionData.estimated[key] || 0;
      }
    });

    return {
      actual: actualTotal,
      estimated: estimatedTotal,
      difference: estimatedTotal - actualTotal
    };
  }

  /**
   * Calculate all totals for the price sheet
   */
  calculateTotals(priceSheetData: VehiclePriceSheet): VehiclePriceSheet {
    // Calculate section subtotals
    const sections = [
      'description', 'refunds', 'local_transport', 'yard_cost', 
      'pre_export_inspection', 'shipping_costs', 'airmail_charges', 
      'isl_internal', 'bank_charges', 'extra_charges'
    ];

    let totalActual = 0;
    let totalEstimated = 0;

    sections.forEach(section => {
      const sectionKey = `${section}_subtotal` as keyof VehiclePriceSheet;
      const subtotal = this.calculateSectionSubtotal({
        actual: priceSheetData.actual_cost_jpy,
        estimated: priceSheetData.estimated_cost_jpy
      });
      
      (priceSheetData as any)[sectionKey] = subtotal;
      totalActual += subtotal.actual;
      totalEstimated += subtotal.estimated;
    });

    // Calculate final totals
    priceSheetData.totals = {
      total_cost: totalActual,
      income: 0, // This would be calculated based on selling price
      refunds: 0, // This would be calculated based on refundable items
      loss: totalEstimated - totalActual
    };

    return priceSheetData;
  }
}
